import "../styles/main.css";
import { useEffect, useState, useMemo, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrackRecordBuilder from "./TrackRecordBuilder";
import ManageTrackRecord from "./ManageTrackRecord/ManageTrackRecord";
import ViewTrackRecord from "./ViewTrackRecord";
import { RangePicker } from "./RangePicker";
import { OverrideDatePicker } from "./OverrideDatePicker";
import { CurrencyPicker, FxTypePicker, CashflowFlagsSwitch } from "./ReportOptionsPicker";
import { advancedFormatQuery } from "./QueryBuilder/AdvancedQueryBuilder";
import { getConfig } from "../config";
import { GENERIC_ERROR_MESSAGE, appErrorAlertSx } from "../constants/errorDisplay";

let appConfig = await getConfig();

export default function CashflowsReportView({ appBusinessUnit, aggData, fieldData, isAdvanced, searchType, setSearchType }) {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(null);
  const [correlationIdTracker, setCorrelationIdTracker] = useState(null);
  const [loader, setLoader] = useState(false);
  const [columns, setColumns] = useState([]);
  const [viewDataGrid, setViewDataGrid] = useState(false);
  const [pnlTip, setPnlTip] = useState(null);
  const [expoTip, setExpoTip] = useState(null);
  const [aggregate, setAggregate] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDateRangeValid, setIsDateRangeValid] = useState(false);
  const [expandHeader, setExpandHeader] = useState(true);

  const [reportingCurrency, setReportingCurrency] = useState("USD");
  const [fxType, setFxType] = useState("End Date");
  const [excludeFxForwards, setExcludeFxForwards] = useState(false);
  const [excludeShorts, setExcludeShorts] = useState(false);
  const [includeVP, setIncludeVP] = useState(false);
  const [overrideDate, setOverrideDate] = useState(null);

  const [selectedTrackRecord, setSelectedTrackRecord] = useState({});
  const [selectedTrackRecordName, setSelectedTrackRecordName] = useState(null);
  const [trackRecordName, setTrackRecordName] = useState("");

  const runButtonContainerRef = useRef(null);
  const [runError, setRunError] = useState(null);

  const [query, setQuery] = useState(appConfig.CASHFLOW_INITIAL_QUERY);
  const [quickQuery, setQuickQuery] = useState(appConfig.CASHFLOW_INITIAL_QUICK_QUERY);

  const toggleHeader = () => setExpandHeader((prev) => !prev);

  useEffect(() => {
    setViewDataGrid(false);
  }, [appBusinessUnit]);

  const handleToggleQuick = () => {
    setSearchType("cs_quick");
  };

  const handleToggleAdvanced = () => {
    setSearchType("cs_advanced");
  };

  const clearRunError = () => {
    setRunError(null);
    setViewDataGrid(false);
    setLoader(false);
  };

  if (runError) {
    return (
      <Stack
        spacing={2}
        direction="column"
        sx={{ flexGrow: 1, overflowX: "hidden", padding: "32px 48px" }}
      >
        <Alert severity="error" variant="outlined" onClose={clearRunError} sx={appErrorAlertSx}>
          <AlertTitle>Error</AlertTitle>
          {GENERIC_ERROR_MESSAGE}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} direction="column" sx={{ flexGrow: 1, overflowX: "hidden", padding: "32px 48px" }}>
      {/* Toggle Buttons */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button
          onClick={handleToggleQuick}
          variant={!isAdvanced ? "contained" : "text"}
          sx={{
            backgroundColor: !isAdvanced ? "#bbdefb" : "transparent",
            color: "#000270",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            padding: "8px 24px",
            boxShadow: !isAdvanced ? "none" : "none",
            "&:hover": {
              backgroundColor: !isAdvanced ? "#90caf9" : "rgba(0, 2, 112, 0.05)",
            },
          }}
        >
          Quick Search
        </Button>
        <Button
          onClick={handleToggleAdvanced}
          variant={isAdvanced ? "contained" : "text"}
          sx={{
            backgroundColor: isAdvanced ? "#bbdefb" : "transparent",
            color: "#000270",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            padding: "8px 24px",
            boxShadow: isAdvanced ? "none" : "none",
            "&:hover": {
              backgroundColor: isAdvanced ? "#90caf9" : "rgba(0, 2, 112, 0.05)",
            },
          }}
        >
          Track Record Search
        </Button>
      </Box>

      <Accordion expanded={expandHeader} sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={toggleHeader}
          className="report-accordion-summary"
        >
          {isAdvanced ? 
            (<Typography variant="h6" className="report-accordion-title">
              Cashflows Advanced Track Record
            </Typography>)
          :
            (<Typography variant="h6" className="report-accordion-title">
              Cashflows Quick Search
            </Typography>)
          }
        </AccordionSummary>

        <Divider />

        <AccordionDetails>
          <Stack spacing={2} direction="column" sx={{ width: "100%" }}>
            {isAdvanced ? (
              <>
                {/* Row 1: Search + name; Row 2: dates/currency; Row 3: Action buttons */}
                <Box sx={{ mt: 1 }}>
                  <ManageTrackRecord
                    setTrackRecordJson={setQuery}
                    setViewDataGrid={setViewDataGrid}
                    appBusinessUnit={appBusinessUnit}
                    trackRecordJson={advancedFormatQuery(query, "JSON")}
                    trackRecordSql={advancedFormatQuery(query, "SQL")}
                    aggregate={aggregate}
                    setAggregate={setAggregate}
                    selectedTrackRecord={selectedTrackRecord}
                    setSelectedTrackRecord={setSelectedTrackRecord}
                    selectedTrackRecordName={selectedTrackRecordName}
                    setSelectedTrackRecordName={setSelectedTrackRecordName}
                    trackRecordName={trackRecordName}
                    setTrackRecordName={setTrackRecordName}
                    isCashflow={true}
                  >
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, my: "auto", height: "70%" }} />
                    <RangePicker
                      startDate={startDate}
                      setStartDate={setStartDate}
                      endDate={endDate}
                      setEndDate={setEndDate}
                      setIsDateRangeValid={setIsDateRangeValid}
                    />
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, my: "auto", height: "70%" }} />
                    <CurrencyPicker
                      reportingCurrency={reportingCurrency}
                      setReportingCurrency={setReportingCurrency}
                    />
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, my: "auto", height: "70%" }} />
                   
                    <Stack spacing={2} direction="row">
                      <FxTypePicker fxType={fxType} setFxType={setFxType} />
                      <OverrideDatePicker overrideDate={overrideDate} setOverrideDate={setOverrideDate} fxType={fxType}/>
                    </Stack>
                    <Box ref={runButtonContainerRef} sx={{ display: "flex", alignItems: "flex-end", ml: 1 }} />
                  </ManageTrackRecord>
                </Box>
                <Divider />

                {/* Cashflow Flags row */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CashflowFlagsSwitch
                    flag={excludeFxForwards}
                    setFlag={setExcludeFxForwards}
                    label='Exclude FX Forwards'
                  />
                  <CashflowFlagsSwitch
                    flag={excludeShorts}
                    setFlag={setExcludeShorts}
                    label='Exclude Shorts'
                  />
                  <CashflowFlagsSwitch
                    flag={includeVP}
                    setFlag={setIncludeVP}
                    label='Include VP Change Cashflows'
                  />
                </Stack>

                {/* Query builder row */}
                <TrackRecordBuilder
                  query={query}
                  quickQuery={quickQuery}
                  setQuery={setQuery}
                  setQuickQuery={setQuickQuery}
                  setLoader={setLoader}
                  setColumns={setColumns}
                  setRows={setRows}
                  setRowCount={setRowCount}
                  setViewDataGrid={setViewDataGrid}
                  setCorrelationIdTracker={setCorrelationIdTracker}
                  appBusinessUnit={appBusinessUnit}
                  aggregateByFields={aggData}
                  fields={fieldData}
                  setPnlTip={setPnlTip}
                  setExpoTip={setExpoTip}
                  startDate={startDate}
                  endDate={endDate}
                  isDateRangeValid={isDateRangeValid}
                  aggregate={aggregate}
                  setAggregate={setAggregate}
                  reportingCurrency={reportingCurrency}
                  fxType={fxType}
                  excludeFxForwards={excludeFxForwards}
                  excludeShorts={excludeShorts}
                  includeVP={includeVP}
                  overrideDate={overrideDate}
                  isCashflow={true}
                  isAdvanced={isAdvanced}
                  runButtonContainerRef={runButtonContainerRef}
                  setRunError={setRunError}
                />
              </>
            ) : (
              <>
                {/* Quick Search: Query builder row (with search field and RUN button) */}
                <TrackRecordBuilder
                  query={query}
                  quickQuery={quickQuery}
                  setQuery={setQuery}
                  setQuickQuery={setQuickQuery}
                  setLoader={setLoader}
                  setColumns={setColumns}
                  setRows={setRows}
                  setRowCount={setRowCount}
                  setViewDataGrid={setViewDataGrid}
                  setCorrelationIdTracker={setCorrelationIdTracker}
                  appBusinessUnit={appBusinessUnit}
                  aggregateByFields={aggData}
                  fields={fieldData}
                  setPnlTip={setPnlTip}
                  setExpoTip={setExpoTip}
                  startDate={startDate}
                  endDate={endDate}
                  isDateRangeValid={isDateRangeValid}
                  aggregate={aggregate}
                  setAggregate={setAggregate}
                  reportingCurrency={reportingCurrency}
                  fxType={fxType}
                  excludeFxForwards={excludeFxForwards}
                  excludeShorts={excludeShorts}
                  includeVP={includeVP}
                  overrideDate={overrideDate}
                  isCashflow={true}
                  isAdvanced={isAdvanced}
                  setRunError={setRunError}
                />

                {/* Date and other options */}
                <Stack direction="row" alignItems="center" flexWrap="wrap">
                  
                  <RangePicker
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    setIsDateRangeValid={setIsDateRangeValid}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: "10px" }} />
                 
                  <CurrencyPicker
                    reportingCurrency={reportingCurrency}
                    setReportingCurrency={setReportingCurrency}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: "10px" }} />
                 
                  
                  <Stack spacing = {2} direction = "row">
                    <FxTypePicker fxType={fxType} setFxType={setFxType} />
                    <OverrideDatePicker overrideDate={overrideDate} setOverrideDate={setOverrideDate} fxType={fxType}/>
                  </Stack>
                </Stack>

                {/* Cashflow Flags row */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CashflowFlagsSwitch
                    flag={excludeFxForwards}
                    setFlag={setExcludeFxForwards}
                    label='Exclude FX Forwards'
                  />
                  <CashflowFlagsSwitch
                    flag={excludeShorts}
                    setFlag={setExcludeShorts}
                    label='Exclude Shorts'
                  />
                  <CashflowFlagsSwitch
                    flag={includeVP}
                    setFlag={setIncludeVP}
                    label='Include VP Change Cashflows'
                  />
                </Stack>
              </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {viewDataGrid && (
        <ViewTrackRecord
          query={query}
          quickQuery={quickQuery}
          rows={rows}
          columns={columns}
          loader={loader}
          correlationIdTracker={correlationIdTracker}
          rowCount={rowCount}
          pnlTip={pnlTip}
          expoTip={expoTip}
          aggregate={aggregate}
          appBusinessUnit={appBusinessUnit}
          isCashflow={true}
          isAdvanced={isAdvanced}
        />
      )}
    </Stack>
  );

}
